// components/analytics/sections/FinanceAnalytics.tsx
import { Row, Col, Card, Typography, Statistic, Tooltip } from "antd";
import { DollarOutlined, ArrowUpOutlined, ArrowDownOutlined, RiseOutlined, InfoCircleOutlined, ThunderboltOutlined, BankOutlined, FileDoneOutlined, GiftOutlined, PercentageOutlined } from "@ant-design/icons";
import { TrendLineChart } from "../components/charts/TrendLineChart";
import { WaterfallChart } from "../components/charts/WaterfallChart";
import type { FinanceAnalytics as FinanceAnalyticsType } from "../../../types/analytics";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface FinanceAnalyticsProps {
  data?: FinanceAnalyticsType | null;
  loading?: boolean;
}

// Componente ModernStatCard para FinanceAnalytics
const ModernStatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  suffix?: string | React.ReactNode;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  info?: string;
  theme?: 'dark' | 'light';
}> = ({ title, value, icon, color, suffix, prefix, precision, loading, info, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)`,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: theme === 'dark' ? '#d9d9d9' : '#595959', 
        margin: 0 
      }}>
        {title}
      </Text>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px' 
      }}>
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ 
              color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
              fontSize: '12px' 
            }} />
          </Tooltip>
        )}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            boxShadow: `0 2px 8px ${color}40`
          }}
        >
          {icon}
        </div>
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        suffix={suffix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

// Componente para cards con información adicional
const InfoStatCard: React.FC<{
  title: string;
  value: number | string;
  info: string;
  icon: React.ReactNode;
  color: string;
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  theme?: 'dark' | 'light';
}> = ({ title, value, info, icon, color, prefix, suffix, precision, loading, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)`,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono e info */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text style={{ 
          fontSize: '13px', 
          fontWeight: 600, 
          color: theme === 'dark' ? '#d9d9d9' : '#595959', 
          margin: 0 
        }}>
          {title}
        </Text>
        <Tooltip title={info}>
          <InfoCircleOutlined style={{ 
            color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
            fontSize: '12px' 
          }} />
        </Tooltip>
      </div>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          boxShadow: `0 2px 8px ${color}40`
        }}
      >
        {icon}
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        suffix={suffix}
        precision={precision}
        loading={loading}
        valueStyle={{
          fontSize: '24px',
          fontWeight: 700,
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

const defaultData: FinanceAnalyticsType = {
  totals: {
    income: 0,
    expenses: 0,
    netBalance: 0,
    totalOperations: 0,
    totalBonusGiven: 0,
    bonusCostPercentage: 0,
  },
  trends: [],
  forecast: [],
  waterfall: [],
  anomalies: [],
};

export const FinanceAnalytics: React.FC<FinanceAnalyticsProps> = ({ data = defaultData, loading = false }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  console.log('[FinanceAnalytics] data received:', JSON.stringify(data?.totals));
  console.log('[FinanceAnalytics] trends:', data?.trends?.length, 'waterfall:', data?.waterfall?.length);

  // Safe computed values
  const incomePercent =
    data.totals.income > 0 ? (data.totals.income / (data.totals.income + data.totals.expenses)) * 100 : 0;

  const expensePercent =
    data.totals.expenses > 0 ? (data.totals.expenses / (data.totals.income + data.totals.expenses)) * 100 : 0;

  const hasTrends = data.trends && Array.isArray(data.trends) && data.trends.length > 0;

  const hasForecast = data.forecast && data.forecast.length > 0;
  const hasWaterfall = data.waterfall && data.waterfall.length > 0;
  const { filters } = useAnalytics();
  
  return (
    <div>
      {/* Cards principales con diseño moderno */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <ModernStatCard
            title={t("analytics.financeAnalytics.cards.totalIncome")}
            value={data.totals.income}
            icon={<ArrowUpOutlined />}
            color="#52c41a"
            suffix={incomePercent > 0 ? `(${incomePercent.toFixed(1)}%)` : ''}
            precision={2}
            loading={loading}
            info="Ingresos totales en el período"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <ModernStatCard
            title={t("analytics.financeAnalytics.cards.totalExpenses")}
            value={data.totals.expenses}
            icon={<ArrowDownOutlined />}
            color="#f5222d"
            suffix={expensePercent > 0 ? `(${expensePercent.toFixed(1)}%)` : ''}
            precision={2}
            loading={loading}
            info="Gastos totales en el período"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <ModernStatCard
            title={t("analytics.financeAnalytics.cards.netBalance")}
            value={data.totals.netBalance}
            icon={<DollarOutlined />}
            color={data.totals.netBalance >= 0 ? "#52c41a" : "#f5222d"}
            precision={2}
            loading={loading}
            info="Balance neto (ingresos - gastos)"
            theme={theme}
          />
        </Col>
      </Row>

      {/* Segunda fila de cards informativos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard
            title="Total Operaciones"
            value={data.totals.totalOperations}
            icon={<FileDoneOutlined />}
            color="#13c2c2"
            loading={loading}
            info="Número total de operaciones (depósitos + retiros)"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <InfoStatCard
            title="Margen de Ganancia"
            value={`${incomePercent.toFixed(1)}%`}
            icon={<RiseOutlined />}
            color="#1890ff"
            loading={loading}
            info="Porcentaje de ingresos vs total de operaciones"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <InfoStatCard
            title="Ratio de Gastos"
            value={`${expensePercent.toFixed(1)}%`}
            icon={<ThunderboltOutlined />}
            color="#fa8c16"
            loading={loading}
            info="Porcentaje de gastos vs total de operaciones"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={4}>
          <InfoStatCard
            title="Eficiencia Financiera"
            value={data.totals.income > 0 ? `${((data.totals.netBalance / data.totals.income) * 100).toFixed(1)}%` : '0%'}
            icon={<BankOutlined />}
            color="#722ed1"
            loading={loading}
            info="Porcentaje del balance neto sobre los ingresos"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={3}>
          <ModernStatCard
            title="Total Bonus ($)"
            value={data.totals.totalBonusGiven}
            icon={<GiftOutlined />}
            color="#722ed1"
            prefix="$"
            precision={2}
            loading={loading}
            info="Resumen de bonus otorgados. Detalle completo en pestaña Gamificaciones."
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={3}>
          <InfoStatCard
            title="Costo Bonus %"
            value={data.totals.bonusCostPercentage?.toFixed(2) ?? '0.00'}
            icon={<PercentageOutlined />}
            color="#ff4d4f"
            suffix="%"
            loading={loading}
            info="Porcentaje de bonus sobre ingresos. Detalle completo en pestaña Gamificaciones."
            theme={theme}
          />
        </Col>
      </Row>

      {/* Charts con diseño moderno */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RiseOutlined style={{ color: '#52c41a' }} />
                {t("analytics.financeAnalytics.charts.incomeTrends.title")}
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {hasTrends ? (
              <TrendLineChart
                data={data.trends
                  .filter((trend) => !filters.accountName || trend.accountName === filters.accountName)
                  .flatMap((trend) => [
                    {
                      date: trend.date,
                      value: trend.income,
                      type: filters.accountName ? "Income" : `Income (${trend.accountName || "Default"})`,
                      accountName: trend.accountName,
                    },
                    {
                      date: trend.date,
                      value: trend.expenses,
                      type: filters.accountName ? "Expenses" : `Expenses (${trend.accountName || "Default"})`,
                      accountName: trend.accountName,
                    },
                  ])}
                xField="date"
                yField="value"
                seriesField="type"
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8c8c8c"
                }}
              >
                {t("analytics.financeAnalytics.charts.incomeTrends.noData")}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarOutlined style={{ color: '#1890ff' }} />
                {t("analytics.financeAnalytics.charts.waterfall.title")}
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {hasWaterfall ? (
              <WaterfallChart
                data={data.waterfall}
                categoryField="category"
                valueField="amount"
                isTotalField="isTotal"
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8c8c8c"
                }}
              >
                {t("analytics.financeAnalytics.charts.waterfall.noData")}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Gráfico de tendencia de operaciones */}
      {hasTrends && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileDoneOutlined style={{ color: '#13c2c2' }} />
                  Tendencia de Operaciones
                </div>
              }
              loading={loading}
              style={{
                background: theme === 'dark'
                  ? '#23272f'
                  : 'rgba(255, 255, 255, 0.95)',
                borderRadius: 16,
                boxShadow: theme === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.32)'
                  : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(12px)',
                border: theme === 'dark'
                  ? '1px solid #23272f'
                  : '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              <TrendLineChart
                data={data.trends
                  .filter((trend) => !filters.accountName || trend.accountName === filters.accountName)
                  .map((trend) => ({
                    date: trend.date,
                    value: trend.operationsCount || 0,
                    type: 'Operaciones',
                  }))}
                xField="date"
                yField="value"
                seriesField="type"
              />
            </Card>
          </Col>
        </Row>
      )}

      {hasForecast && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RiseOutlined style={{ color: '#722ed1' }} />
                  {t("analytics.financeAnalytics.charts.forecast.title")}
                </div>
              }
              loading={loading}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 16,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              <TrendLineChart
                data={data.forecast.map((item) => ({
                  date: item.date,
                  value: item.predicted,
                  type: 'Forecast',
                }))}
                xField="date"
                yField="value"
                seriesField="type"
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};
